import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Chip,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  Person as PersonIcon,
  ShoppingCart as ShoppingCartIcon,
  Paid as PaidIcon,
  TableChart as TableIcon,
  PictureAsPdf as PdfIcon
} from '@mui/icons-material';
import { CustomerStats } from '../models/CustomerStats';
import customerAnalyticsService from '../services/customerAnalyticsService';
import type { CustomerAnalyticsAggregateScope } from '../services/customerAnalyticsScopeDailyService';
import customerService from '../services/customerService';
import DateRangePicker from '../components/DateRangePicker';
import DateUtils from '../utils/dateUtils';
import CustomerProfileDialog from '../components/CustomerProfileDialog';
import type { Customer } from '../models/Customer';
import { useAdminScope } from '../contexts/AdminScopeContext';
import { describeActiveScope, resolveScopedStoreIds } from '../utils/adminScopeUtils';

type SortField = 'totalOrders' | 'totalSpent' | 'averageOrderValue' | 'orderFrequency' | 'lastOrderDate' | 'clientName';
type SortDirection = 'asc' | 'desc';
type TimePeriod = 'week' | 'month' | 'quarter' | 'semester' | 'year' | 'custom';

interface QuickStats {
  totalCustomers: number;
  activeCustomers: number;
  newCustomers: number;
  vipCustomers: number;
  averageOrdersPerCustomer: number;
  customerRetentionRate: number;
}

const buildQuickStats = (customers: CustomerStats[]): QuickStats => {
  const totalCustomers = customers.length;
  const activeCustomers = customers.filter((customer) => {
    const daysSinceLastOrder = (Date.now() - customer.lastOrderDate.getTime()) / (1000 * 60 * 60 * 24);
    return daysSinceLastOrder <= 30;
  }).length;
  const newCustomers = customers.filter((customer) => {
    const daysSinceFirst = (Date.now() - customer.firstOrderDate.getTime()) / (1000 * 60 * 60 * 24);
    return daysSinceFirst <= 30;
  }).length;
  const vipCustomers = customers.filter((customer) => customer.totalOrders > 20).length;
  const averageOrdersPerCustomer = totalCustomers > 0
    ? customers.reduce((sum, customer) => sum + customer.totalOrders, 0) / totalCustomers
    : 0;
  const returningCustomers = customers.filter((customer) => customer.totalOrders > 1).length;
  const customerRetentionRate = totalCustomers > 0
    ? (returningCustomers / totalCustomers) * 100
    : 0;

  return {
    totalCustomers,
    activeCustomers,
    newCustomers,
    vipCustomers,
    averageOrdersPerCustomer,
    customerRetentionRate,
  };
};

const CustomerAnalytics: React.FC = () => {
  const {
    loading: scopeLoading,
    activeScopeType,
    selectedGroupId,
    selectedStoreId,
    selectedGroup,
    selectedStore,
    stores,
  } = useAdminScope();
  const [customers, setCustomers] = useState<CustomerStats[]>([]);
  const [filteredCustomers, setFilteredCustomers] = useState<CustomerStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortField, setSortField] = useState<SortField>('totalOrders');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [timePeriod, setTimePeriod] = useState<TimePeriod>('month');
  const [customDateRange, setCustomDateRange] = useState<{ start: Date; end: Date } | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [quickStats, setQuickStats] = useState<QuickStats | null>(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [profileCustomer, setProfileCustomer] = useState<Customer | null>(null);
  const totalRevenue = customers.reduce((sum, c) => sum + c.totalSpent, 0);
  const scopedStoreIds = useMemo(
    () =>
      resolveScopedStoreIds({
        activeScopeType,
        selectedGroupId,
        selectedStoreId,
        stores,
      }),
    [activeScopeType, selectedGroupId, selectedStoreId, stores]
  );
  const scopeSummary = useMemo(
    () =>
      describeActiveScope({
        activeScopeType,
        selectedGroupId,
        selectedStoreId,
        stores,
      }),
    [activeScopeType, selectedGroupId, selectedStoreId, stores]
  );
  const aggregateScope = useMemo<CustomerAnalyticsAggregateScope>(
    () =>
      selectedStoreId
        ? { scopeType: 'store', scopeId: selectedStoreId }
        : selectedGroupId
          ? { scopeType: 'group', scopeId: selectedGroupId }
          : { scopeType: 'global', scopeId: 'global' },
    [selectedGroupId, selectedStoreId]
  );
  const loadRequestIdRef = useRef(0);
  const isScopeReady = useMemo(() => {
    if (scopeLoading) {
      return false;
    }

    if (activeScopeType === 'global') {
      return true;
    }

    return Boolean(selectedStoreId || selectedGroupId) || stores.length === 0;
  }, [scopeLoading, activeScopeType, selectedStoreId, selectedGroupId, stores.length]);

  // Obtenir la plage de dates pour une période donnée
  const getDateRangeForPeriod = (period: TimePeriod) => {
    const now = new Date();
    
    switch (period) {
      case 'week':
        return DateUtils.getThisWeekRange();
      case 'month':
        return DateUtils.getThisMonthRange();
      case 'quarter':
        // Trimestre actuel
        const quarterStart = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1);
        const quarterEnd = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3 + 3, 0);
        return { start: quarterStart, end: quarterEnd };
      case 'semester':
        // Semestre actuel
        const semesterStart = new Date(now.getFullYear(), now.getMonth() < 6 ? 0 : 6, 1);
        const semesterEnd = new Date(now.getFullYear(), now.getMonth() < 6 ? 5 : 11, 0);
        return { start: semesterStart, end: semesterEnd };
      case 'year':
        return DateUtils.getThisYearRange();
      default:
        return null;
    }
  };

  // Charger les données
  const loadCustomerData = useCallback(async () => {
    const requestId = ++loadRequestIdRef.current;

    try {
      setLoading(true);
      setError(null);

      if (!isScopeReady) {
        return;
      }

      if (scopedStoreIds !== null && scopedStoreIds.length === 0) {
        if (requestId !== loadRequestIdRef.current) {
          return;
        }
        setCustomers([]);
        setQuickStats(buildQuickStats([]));
        return;
      }

      let dateRange = null;
      if (timePeriod === 'custom' && customDateRange) {
        dateRange = customDateRange;
      } else if (timePeriod !== 'custom') {
        dateRange = getDateRangeForPeriod(timePeriod);
      }

      const scope =
        selectedStoreId
          ? { storeId: selectedStoreId, aggregateScope }
          : scopedStoreIds !== null
            ? { allowedStoreIds: scopedStoreIds || [], aggregateScope }
            : { aggregateScope };

      const customerData = await customerAnalyticsService.calculateCustomerStats(
        dateRange || undefined,
        scope
      );

      if (requestId !== loadRequestIdRef.current) {
        return;
      }
      setCustomers(customerData);
      setQuickStats(buildQuickStats(customerData));
    } catch (err) {
      if (requestId !== loadRequestIdRef.current) {
        return;
      }
      console.error('Erreur lors du chargement des données clients:', err);
      setError('Impossible de charger les données des clients');
    } finally {
      if (requestId === loadRequestIdRef.current) {
        setLoading(false);
      }
    }
  }, [
    aggregateScope,
    customDateRange,
    isScopeReady,
    scopedStoreIds,
    selectedStoreId,
    timePeriod,
  ]);

  // Effet pour charger les données au montage et lors des changements de période
  useEffect(() => {
    if (!isScopeReady) {
      return;
    }

    void loadCustomerData();
  }, [loadCustomerData, isScopeReady]);

  // Effet pour trier et filtrer les clients
  useEffect(() => {
    let sorted = [...customers];

    // Tri
    sorted.sort((a, b) => {
      let aValue: any = a[sortField];
      let bValue: any = b[sortField];

      if (sortField === 'lastOrderDate') {
        aValue = aValue.getTime();
        bValue = bValue.getTime();
      } else if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (sortDirection === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    setFilteredCustomers(sorted);
  }, [customers, sortField, sortDirection]);

  useEffect(() => {
    setPage(0);
  }, [filteredCustomers.length, timePeriod, selectedStoreId, selectedGroupId]);

  // Gérer le changement de tri
  const handleSort = (field: SortField) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  // Gérer le changement de période
  const handleTimePeriodChange = (period: TimePeriod) => {
    setTimePeriod(period);
    if (period !== 'custom') {
      setCustomDateRange(null);
    }
  };

  const pagedCustomers = useMemo(
    () => filteredCustomers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredCustomers, page, rowsPerPage]
  );

  // Exporter en CSV (MAD corrigé côté service)
  const handleExport = async () => {
    try {
      const csvContent = await customerAnalyticsService.exportCustomerStatsToCSVWithNames(filteredCustomers);
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `clients_analytics_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Erreur lors de l\'export CSV:', err);
    }
  };

  // Exporter en XLS (HTML compatible Excel)
  const handleExportXLS = async () => {
    try {
      const html = await customerAnalyticsService.exportCustomerStatsToXLSWithNames(filteredCustomers);
      const blob = new Blob([html], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `clients_analytics_${new Date().toISOString().split('T')[0]}.xlsx`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Erreur lors de l\'export XLSX:', err);
    }
  };

  // Exporter en PDF (ouvre une page imprimable → Enregistrer au format PDF)
  const handleExportPDF = async () => {
    try {
      const html = await customerAnalyticsService.exportCustomerStatsToPrintableHTMLWithNames(filteredCustomers);
      const blob = new Blob([html], { type: 'text/html;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      console.error('Erreur lors de l\'export PDF:', err);
    }
  };

  // Formater la période pour l'affichage
  const formatTimePeriod = (period: TimePeriod): string => {
    switch (period) {
      case 'week': return 'Cette semaine';
      case 'month': return 'Ce mois';
      case 'quarter': return 'Ce trimestre';
      case 'semester': return 'Ce semestre';
      case 'year': return 'Cette année';
      case 'custom': return 'Période personnalisée';
      default: return '';
    }
  };

  const openProfile = async (s: CustomerStats) => {
    // Ouvrir d'abord le dialog avec un placeholder (nom + téléphone) puis enrichir avec Firestore (email + adresses)
    const name = (s.clientName || 'Client').trim();
    const parts = name.split(' ');
    const firstName = parts.shift() || 'Client';
    const lastName = parts.join(' ');
    const placeholder: Customer = {
      id: s.userId,
      firstName,
      lastName,
      phone: s.clientPhone || '',
      email: '',
      isBanned: undefined,
      createdAt: undefined
    };
    setProfileCustomer(placeholder);
    setProfileOpen(true);

    try {
      const full = await customerService.getCustomerById(s.userId);
      if (full) {
        // Remplacer par la version complète (email, adresses, isBanned…)
        setProfileCustomer(full);
      }
    } catch {
      // ignorer en gardant le placeholder
    }
  };

  // Obtenir la couleur du chip selon le nombre de commandes
  const getCustomerTypeChip = (totalOrders: number) => {
    if (totalOrders > 20) {
      return <Chip label="VIP" color="error" size="small" />;
    } else if (totalOrders >= 5) {
      return <Chip label="Régulier" color="warning" size="small" />;
    } else if (totalOrders >= 2) {
      return <Chip label="Occasionnel" color="info" size="small" />;
    } else {
      return <Chip label="Nouveau" color="default" size="small" />;
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Analyse des Clients
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Alert severity="info" sx={{ mb: 3 }}>
        Scope actif: {scopeSummary}
        {selectedGroup ? ` · Groupe ${selectedGroup.name}` : ''}
        {selectedStore ? ` · Magasin ${selectedStore.name}` : ''}
      </Alert>

      {/* Statistiques rapides */}
      {quickStats && (
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
          <Card sx={{ minWidth: 180, flex: 1 }}>
            <CardContent>
              <Box display="flex" alignItems="center">
                <PersonIcon color="primary" sx={{ mr: 1 }} />
                <Box>
                  <Typography variant="h6">{quickStats.totalCustomers}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Clients
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
          <Card sx={{ minWidth: 180, flex: 1 }}>
            <CardContent>
              <Box display="flex" alignItems="center">
                <TrendingUpIcon color="success" sx={{ mr: 1 }} />
                <Box>
                  <Typography variant="h6">{quickStats.activeCustomers}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Clients Actifs
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
          <Card sx={{ minWidth: 180, flex: 1 }}>
            <CardContent>
              <Box display="flex" alignItems="center">
                <PersonIcon color="info" sx={{ mr: 1 }} />
                <Box>
                  <Typography variant="h6">{quickStats.newCustomers}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Nouveaux Clients
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
          <Card sx={{ minWidth: 180, flex: 1 }}>
            <CardContent>
              <Box display="flex" alignItems="center">
                <PaidIcon color="secondary" sx={{ mr: 1 }} />
                <Box>
                  <Typography variant="h6">{totalRevenue.toFixed(2)} MAD</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total dépensé (période)
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
          <Card sx={{ minWidth: 180, flex: 1 }}>
            <CardContent>
              <Box display="flex" alignItems="center">
                <ShoppingCartIcon color="warning" sx={{ mr: 1 }} />
                <Box>
                  <Typography variant="h6">{quickStats.averageOrdersPerCustomer.toFixed(1)}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Commandes/Client
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
          <Card sx={{ minWidth: 180, flex: 1 }}>
            <CardContent>
              <Box display="flex" alignItems="center">
                <TrendingUpIcon color="secondary" sx={{ mr: 1 }} />
                <Box>
                  <Typography variant="h6">{quickStats.customerRetentionRate.toFixed(1)}%</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Rétention
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Filtres et contrôles */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <Box sx={{ minWidth: 200 }}>
            <FormControl fullWidth>
              <InputLabel>Période</InputLabel>
              <Select
                value={timePeriod}
                label="Période"
                onChange={(e) => handleTimePeriodChange(e.target.value as TimePeriod)}
              >
                <MenuItem value="week">Cette semaine</MenuItem>
                <MenuItem value="month">Ce mois</MenuItem>
                <MenuItem value="quarter">Ce trimestre</MenuItem>
                <MenuItem value="semester">Ce semestre</MenuItem>
                <MenuItem value="year">Cette année</MenuItem>
                <MenuItem value="custom">Période personnalisée</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {timePeriod === 'custom' && (
            <Box sx={{ minWidth: 300 }}>
              <DateRangePicker
                value={customDateRange || undefined}
                onChange={(dateRange) => {
                  setCustomDateRange(dateRange || null);
                }}
                label="Période personnalisée"
                showQuickOptions={false}
              />
            </Box>
          )}

          <Box sx={{ flexGrow: 1 }} />

          <Tooltip title="Actualiser">
            <IconButton onClick={loadCustomerData} color="primary">
              <RefreshIcon />
            </IconButton>
          </Tooltip>

          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleExport}
          >
            Exporter CSV
          </Button>

          <Button
            variant="outlined"
            startIcon={<TableIcon />}
            onClick={handleExportXLS}
          >
            Exporter XLS
          </Button>

          <Button
            variant="outlined"
            startIcon={<PdfIcon />}
            onClick={handleExportPDF}
          >
            Exporter PDF
          </Button>
        </Box>
      </Paper>

      {/* Tableau des clients */}
      <Paper>
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h6">
            Top Clients - {formatTimePeriod(timePeriod)} ({filteredCustomers.length} clients)
          </Typography>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={sortField === 'clientName'}
                    direction={sortField === 'clientName' ? sortDirection : 'asc'}
                    onClick={() => handleSort('clientName')}
                  >
                    Client
                  </TableSortLabel>
                </TableCell>
                <TableCell>Téléphone</TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={sortField === 'totalOrders'}
                    direction={sortField === 'totalOrders' ? sortDirection : 'asc'}
                    onClick={() => handleSort('totalOrders')}
                  >
                    Commandes
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={sortField === 'totalSpent'}
                    direction={sortField === 'totalSpent' ? sortDirection : 'asc'}
                    onClick={() => handleSort('totalSpent')}
                  >
                    Total Dépensé
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={sortField === 'averageOrderValue'}
                    direction={sortField === 'averageOrderValue' ? sortDirection : 'asc'}
                    onClick={() => handleSort('averageOrderValue')}
                  >
                    Panier Moyen
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={sortField === 'lastOrderDate'}
                    direction={sortField === 'lastOrderDate' ? sortDirection : 'asc'}
                    onClick={() => handleSort('lastOrderDate')}
                  >
                    Dernière Commande
                  </TableSortLabel>
                </TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Préférences</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pagedCustomers.map((customer, index) => (
                <TableRow key={customer.userId} hover onClick={() => openProfile(customer)} sx={{ cursor: 'pointer' }}>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {customer.clientName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        #{page * rowsPerPage + index + 1}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{customer.clientPhone}</TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight="medium">
                      {customer.totalOrders}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight="medium">
                      {customer.totalSpent.toFixed(2)} MAD
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    {customer.averageOrderValue.toFixed(2)} MAD
                  </TableCell>
                  <TableCell align="right">
                    {customer.lastOrderDate.toLocaleDateString('fr-FR')}
                  </TableCell>
                  <TableCell>
                    {getCustomerTypeChip(customer.totalOrders)}
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="caption" display="block">
                        {customer.preferredPaymentMethod === 'cash' ? '💵 Espèces' : '💳 Carte'}
                      </Typography>
                      <Typography variant="caption" display="block">
                        {customer.preferredDeliveryMethod === 'delivery' ? '🚚 Livraison' : '🏪 À emporter'}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={filteredCustomers.length}
          page={page}
          onPageChange={(_event, nextPage) => setPage(nextPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[25, 50, 100, 250]}
          labelRowsPerPage="Lignes"
        />

        {filteredCustomers.length === 0 && !loading && (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">
              Aucun client trouvé pour la période sélectionnée
            </Typography>
          </Box>
        )}
      </Paper>

      <CustomerProfileDialog
        open={profileOpen}
        customer={profileCustomer}
        onClose={() => { setProfileOpen(false); setProfileCustomer(null); }}
      />
    </Box>
  );
};

export default CustomerAnalytics;
